import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, TableCell } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { makeStyles } from '@material-ui/core/styles';
import get from 'lodash/get';
import React from 'react';
import { useTranslation } from 'react-i18next';

import DetailsButton from 'components/Buttons/DetailsButton';
import { LISTS_COMMON_STYLES, ListStyleProps } from 'style/lists';
import { ListableEntity } from 'types';
import { formatDateForDisplay, formatDateToMonthYear, formatHourForDisplay, toDisplayHour } from 'utils/date';
import { formatKgFromG, formatTonsFromKg } from 'utils/weight';
import ActionButton from 'components/Buttons/ActionButton';
import { EtemTooltip } from 'components/EtemTooltip';
import { AvailableListAction } from './GenericList';

const useStyles = makeStyles(LISTS_COMMON_STYLES);

export type EntityColumnType =
  | 'date'
  | 'action'
  | 'dateMonth'
  | 'quantity'
  | 'bool'
  | 'hour'
  | 'weightKg'
  | 'number'
  | 'chipsConfigured';

export type EntityColumn = {
  columnPath: string;
  type?: EntityColumnType;
  headerTranslationKey: string;
  chipsConfiguration?: {
    [columnValue: string]: {
      translationKeyFromValue: string;
      backgroundColor: string;
      hideIfPropertiesHaveValues?: { [key: string]: any };
      tooltipTextTranslationKey?: string;
    };
  };
  disableSort?: boolean;
  style: ListStyleProps;
};

export interface TableCellWithConfigurableStyleProps {
  entity: ListableEntity;
  column: EntityColumn;
  availableActions?: Array<AvailableListAction>;
  detailsLink: string;
  onClick?: () => void;
  noHiddenHour?: boolean;
}

const TableCellWithConfigurableStyle: React.FC<TableCellWithConfigurableStyleProps> = ({
  entity,
  column,
  availableActions,
  detailsLink,
  onClick,
  noHiddenHour,
}) => {
  const classes = useStyles(column.style);
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLAnchorElement>(null);

  const handleClick = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  switch (column.type) {
    case 'date':
      return (
        <TableCell onClick={onClick} className={classes.cell}>
          {formatDateForDisplay(get(entity, column.columnPath))}
        </TableCell>
      );
    case 'hour':
      return (
        <TableCell onClick={onClick} className={`${classes.cell} ${classes.centeredCell}`}>
          {noHiddenHour
            ? toDisplayHour(get(entity, column.columnPath))
            : formatHourForDisplay(get(entity, column.columnPath))}
        </TableCell>
      );
    case 'dateMonth':
      return (
        <TableCell onClick={onClick} className={`${classes.cell} ${classes.monthYearCell}`}>
          {formatDateToMonthYear(get(entity, column.columnPath))}
        </TableCell>
      );
    case 'action':
      return (
        <TableCell ref={anchorRef} className={classes.cell}>
          <div className={classes.actions}>
            {availableActions && availableActions.length > 1 ? (
              <>
                <DetailsButton linkUrl={`${detailsLink}/${entity.id}`} />
                <Popper
                  anchorEl={anchorRef.current}
                  open={open}
                  role={undefined}
                  transition
                  modifiers={{
                    preventOverflow: {
                      enabled: true,
                    },
                    hide: {
                      enabled: false,
                    },
                  }}
                >
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                      <Paper className={classes.userMenu} square elevation={0} variant="outlined">
                        <ClickAwayListener onClickAway={handleClose}>
                          <MenuList id="menu-list-grow">
                            {availableActions.slice(1).map(action => (
                              <MenuItem
                                disabled={action.disabled}
                                onClick={() => {
                                  setOpen(false);
                                  action.onClick();
                                }}
                                key={action.title}
                              >
                                {action.title}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
                {availableActions?.[0]?.action}
                <ActionButton
                  data-qa-id="action-button"
                  aria-label={open ? 'close' : 'open'}
                  aria-controls={open ? 'menu-list-grow' : undefined}
                  aria-haspopup="true"
                  onClick={handleClick}
                  icon={MoreVertIcon}
                  className={classes.moreActionButton}
                ></ActionButton>
              </>
            ) : (
              availableActions?.[0]?.action
            )}
          </div>
        </TableCell>
      );
    case 'number':
      return (
        <TableCell onClick={onClick} className={`${classes.cell} ${classes.centeredCell}`}>
          {get(entity, column.columnPath) ?? '-'}
        </TableCell>
      );
    case 'quantity':
      return (
        <TableCell onClick={onClick} className={`${classes.cell} ${classes.rightAlignedCell}`}>
          {formatTonsFromKg(get(entity, column.columnPath))}
        </TableCell>
      );
    case 'weightKg':
      return (
        <TableCell onClick={onClick} className={`${classes.cell} ${classes.rightAlignedCell}`}>
          {formatKgFromG(get(entity, column.columnPath))}
        </TableCell>
      );
    case 'bool':
      return (
        <TableCell onClick={onClick} className={`${classes.cell} ${classes.centeredCell}`}>
          {get(entity, column.columnPath) ? t('yes') : t('no')}
        </TableCell>
      );
    case 'chipsConfigured':
      const columnValue = get(entity, column.columnPath);
      const chipsConfiguration = column.chipsConfiguration?.[columnValue];
      let showChips = true;

      if (undefined === chipsConfiguration) return <div></div>;

      const hasTooltip = undefined !== chipsConfiguration.tooltipTextTranslationKey ? true : false;

      if (chipsConfiguration.hideIfPropertiesHaveValues) {
        for (const [property, value] of Object.entries(chipsConfiguration.hideIfPropertiesHaveValues)) {
          const propertyToCheck = get(entity, property);
          showChips = showChips && Boolean(propertyToCheck !== value);
        }
      }

      return (
        <TableCell onClick={onClick} className={`${classes.cell} ${classes.centeredCell}`}>
          {showChips && (
            <EtemTooltip
              title={
                <>
                  <p className={classes.tooltipText}>
                    {undefined === chipsConfiguration.tooltipTextTranslationKey
                      ? ''
                      : t(chipsConfiguration.tooltipTextTranslationKey)}
                  </p>
                </>
              }
              disableHoverListener={!hasTooltip}
              placement="left"
              arrow
            >
              <div
                onClick={onClick}
                className={`${classes.chipsConfigured}`}
                style={{ backgroundColor: chipsConfiguration.backgroundColor }}
              >
                {t(chipsConfiguration.translationKeyFromValue)}
              </div>
            </EtemTooltip>
          )}
        </TableCell>
      );
    default:
      return <TableCell onClick={onClick}>{get(entity, column.columnPath, '-')}</TableCell>;
  }
};

export default TableCellWithConfigurableStyle;
